import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { InventoryCounterInline } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/InventoryCounterInline';
import { ANY_SPORT_MY_CLUB_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_CLUB_SHOP_INVENTORY } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BuyButton } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/BuyButton';
import { useOnBuyButtonClick } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/BuyButton/useOnBuyButtonClick';
import { Details } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/Details';
import { ItemImagePreview } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/ItemImagePreview';
import { Requirements } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/Requirements';

import { CommonCardSwapContent_clubShopItem } from './__generated__/index.graphql';

type Props = {
  item: CommonCardSwapContent_clubShopItem;

  inventory: boolean;
  onBuy?: () => void;
  userCoinBalance: number;
  userSlug?: string;
  onClose: () => void;
};

export const CommonCardSwapContent = ({
  item,
  inventory,
  onBuy,
  userCoinBalance,
  userSlug = '',
  onClose,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const [currentStep, setCurrentStep] = useState<'step1' | 'step2'>(
    inventory ? 'step2' : 'step1'
  );
  const { onBuyButtonClick, loading } = useOnBuyButtonClick(
    () => setCurrentStep('step2'),
    onBuy
  );

  return (
    <>
      <ItemImagePreview
        pictureUrl={item.pictureUrl}
        name={item.name}
        type={item.position}
      />
      <Details item={item} inventory={inventory} />
      {currentStep === 'step1' && (
        <>
          <Vertical center>
            {item.disabled && <Requirements item={item} />}
            {!item.disabled && (
              <InventoryCounterInline
                name={item.name}
                amount={item.myBalance}
              />
            )}
          </Vertical>
          <BuyButton
            item={item}
            onClick={() => onBuyButtonClick(item.id)}
            loading={loading}
            userCoinBalance={userCoinBalance}
          />
        </>
      )}
      {currentStep === 'step2' && (
        <Horizontal center gap={2}>
          <Button
            to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
              params: { slug: userSlug },
            })}
            onClick={() => {
              onClose();
            }}
            color="primary"
            size="medium"
          >
            <FormattedMessage {...glossary.goToMyCards} />
          </Button>
          <Button
            to={generateSportContextPath(ANY_SPORT_CLUB_SHOP_INVENTORY)}
            onClick={() => {
              onClose();
            }}
            color="tertiary"
            size="medium"
          >
            <FormattedMessage {...glossary.viewMyItems} />
          </Button>
        </Horizontal>
      )}
    </>
  );
};

CommonCardSwapContent.fragments = {
  clubShopItem: gql`
    fragment CommonCardSwapContent_clubShopItem on ClubShopItem {
      ... on ShopItemInterface {
        id
        pictureUrl
        name
        position
        myLimitResetAt
        myBalance
        expirationDate
      }
      ...Details_shopItem
      ...ConsumableCta_shopItem
      ...Requirements_clubShopItem
    }
    ${Details.fragments.shopItem}
    ${Requirements.fragments.clubShopItem}
  ` as TypedDocumentNode<CommonCardSwapContent_clubShopItem>,
};
